// review-report-promotion-wiring.ts
//
// The supervisor-loop seam for review-report-promotion.ts, split out to keep
// supervisor-loop.ts inside the 200-line budget (same convention as every
// other supervisor-loop-*.ts split) and to keep the "when does promotion
// happen" predicate in one readable place.
//
// Promotion runs ONLY when all of these hold:
//   - the ticket's adapter is `review-local-v1` (no other adapter produces a
//     review report, and promoting one from, say, an implement ticket would
//     stage a report for a review that never ran);
//   - the attempt routed to `done` with a commit — a rejected/retrying
//     attempt has no candidate SHA, and promoting its report would stage a
//     review of code that was never committed.
//
// Anything else is a silent no-op: the staging file stays absent and the
// release flow keeps answering `waiting_review_report`, which is the honest
// answer when no review report was produced. The report itself is read from
// a fixed path inside the attempt's own staging dir — see
// review-report-promotion.ts's header for why it is NOT routed through
// `evidence[]`.
import { randomUUID } from "node:crypto";
import type { MissionStore } from "./mission-store.js";
import {
  type PromoteWorkerReviewReportResult,
  promoteWorkerReviewReportToControlRoot,
} from "./review-report-promotion.js";
import type { Ticket } from "./schemas/ticket-schema.js";

/**
 * The adapter whose report is PROMOTED into the control root, i.e. the one
 * whose verdict reaches the push gate (`review-staging/<sha>.json` →
 * supervisor-release-flow.ts).
 *
 * Still local-only after C2 added a second reviewer, on purpose: routing a
 * second verdict into the gate changes WHAT BLOCKS A PUSH, and the rule for
 * merging two verdicts (highest risk wins? both must be low?) is a decision a
 * person owns. See phase-03 report, câu hỏi treo 2.
 */
export const REVIEW_LOCAL_ADAPTER_ID = "review-local-v1";

/**
 * The adapters whose attempt MUST declare `review_report` in its result.
 *
 * Hai câu hỏi VẪN khác nhau — "phiên này có làm việc được giao không" vs
 * "verdict này có vào gate push không" — nhưng từ phase-07 (2026-09-16) câu
 * TRẢ LỜI của chúng trùng nhau, nên hằng này gác cả hai:
 *   - verifier bắt buộc khai verdict (`supervisor-attempt-cycle.ts`);
 *   - promotion đưa verdict đó tới gate push (hàm dưới + `review-report-merge.ts`).
 *
 * ⚠️ Comment cũ ở đây ghi "the two must not be merged... promotion is currently
 * true of only the first". Câu đó ĐÚNG cho tới P3 và SAI từ phase-07: để
 * nguyên thì người thêm adapter thứ ba sẽ tách lại, và verdict của adapter mới
 * rơi vào hư không đúng như lỗ H1 nửa B vừa đóng.
 *
 * Ngày nào có adapter BẮT BUỘC khai verdict mà KHÔNG được vào gate push, phải
 * tách lại thành hai hằng ngay — `test_both_reviewer_adapters_are_covered_by_
 * the_promotion_gate` là chỗ bắt được điều đó.
 *
 * H1 nửa A (code review P3, 2026-09-16): keying the verifier flag on
 * `REVIEW_LOCAL_ADAPTER_ID` alone let a `review-clean-v1` attempt reach `done`
 * having declared no verdict at all — while `workflows/autopilot/review-clean.md`
 * step 7 promises the worker it would be rejected. Prose enforced by nobody,
 * the same shape mission ME-2026-dc845a measured for the first reviewer.
 */
export const REVIEW_REPORT_REQUIRED_ADAPTER_IDS: readonly string[] = [
  REVIEW_LOCAL_ADAPTER_ID,
  "review-clean-v1",
];

export interface PromoteReviewReportAfterDoneRouteInput {
  controlRoot: string;
  repoRoot: string;
  ticket: Ticket;
  cycle: { kind: string; runId?: string; sessionResult?: { result?: unknown } };
  route: { kind: string; commitHeadSha?: string | undefined };
  /**
   * P21 (code-reviewer follow-up): journal seam. Without it the promoter's
   * answer was awaited and discarded, so from outside the process "the
   * report was promoted" and "there was nothing to promote" looked
   * IDENTICAL — and "no record at all" is precisely what a step that never
   * ran looks like. Same argument P19 made for VERIFYING. Optional so the
   * predicate stays unit-testable without a store.
   */
  store?: Pick<MissionStore, "commit">;
}

/** Journal outcome string for a promotion attempt — `promoted_with_source_mismatch` is still a promotion, just one worth reading twice. */
function promotionOutcome(result: PromoteWorkerReviewReportResult): string {
  if (result.status !== "promoted") return result.status;
  return result.sourceMismatch ? "promoted_with_source_mismatch" : "promoted";
}

export async function promoteReviewReportAfterDoneRoute(
  input: PromoteReviewReportAfterDoneRouteInput,
): Promise<PromoteWorkerReviewReportResult | undefined> {
  // H1 nửa B (user chốt luật gộp 2026-09-16): CẢ HAI reviewer được promote,
  // mỗi bên ra file riêng (`reviewStagingFileName`). Trước đây chỉ
  // `review-local-v1` qua được cửa này, nên verdict của T-9002 — phiên Claude
  // thật, tốn tiền thật, bị P3 BẮT BUỘC phải khai — không ai đọc.
  if (!REVIEW_REPORT_REQUIRED_ADAPTER_IDS.includes(input.ticket.command_adapter)) return undefined;
  if (input.route.kind !== "done") return undefined;

  const candidateSha = input.route.commitHeadSha;
  if (candidateSha === undefined || candidateSha === "") return undefined;
  if (input.cycle.kind !== "verified" || input.cycle.runId === undefined) return undefined;

  const result = await promoteWorkerReviewReportToControlRoot({
    controlRoot: input.controlRoot,
    repoRoot: input.repoRoot,
    runId: input.cycle.runId,
    candidateSha,
    // P21: the report the schema validated and the verifier gated on. The
    // staging file is only read when the result carries none — which the
    // verifier already rejects for reviewer adapters — so it is a
    // cross-check copy, not a fallback (H12 follow-up).
    reportFromResult: (input.cycle.sessionResult?.result as { review_report?: unknown } | undefined)
      ?.review_report,
    adapterId: input.ticket.command_adapter,
  });

  if (input.store !== undefined) {
    const operationId = `review-report-promotion-${input.cycle.runId}`;
    await input.store.commit([
      {
        event_id: randomUUID(),
        operation_id: operationId,
        delta: {
          kind: "operation_result",
          operation_id: operationId,
          operation_kind: "review_report_promotion",
          outcome: promotionOutcome(result),
          candidate_sha: candidateSha,
          ...(result.status === "no_report" ? { reason: result.reason } : {}),
        },
      },
      // Code review P3 H1: which candidate THIS review session settled. A
      // later candidate (redirect, follow-up ticket) no longer reads as
      // "already reviewed" — see supervisor-candidate-review-settlement.ts.
      {
        event_id: randomUUID(),
        delta: {
          kind: "candidate_review_finished",
          ticket: input.ticket.id,
          candidate_sha: candidateSha,
        },
      },
    ]);
  }

  return result;
}
